import { faChartMixed } from "@fortawesome/pro-regular-svg-icons";
import { v4 } from "uuid";

import { AnalyticsCopilotSidebar } from "src/analytics/copilot/AnalyticsCopilotSidebar";
import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import type { DashboardPinning } from "src/analytics/copilot/LiveAnalyticsMessages";
import { nextPinLayout } from "src/analytics/dashboard/dashboardLayout";
import { useJustPinned } from "src/analytics/dashboard/JustPinnedContext";
import { MAX_PINS, useDashboard } from "src/analytics/dashboard/useDashboard";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import {
  useCreateDashboard,
  useListDashboardsQuery,
} from "src/api/analytics-dashboard/dashboard";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { useCaseAnalyticsChat } from "src/cases/analytics/m1/useCaseAnalyticsChat";
import type { ChartDefinition, Pin } from "src/clients/analytics-dashboard";
import { Realm } from "src/clients/analytics-dashboard";
import { toastActions } from "src/design-system/Toast/utils";
import type { Environment } from "src/hooks/useEnvironment";
import { logger } from "src/utils/logger";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "How many cases were created in the last 30 days?",
    prompt: "How many cases were created in the last 30 days?",
  },
  {
    label: "Show me cases created per day for the last month.",
    prompt: "Show me cases created per day for the last month.",
  },
  {
    label: "Which actions fail most frequently?",
    prompt: "Which actions fail most frequently?",
  },
];

export const CaseCopilotSidebar: React.FC<{
  onClose: () => void;
  env: Environment;
  wsId: string;
  labelDictionaries: LabelDictionaries;
  dashboardId?: string;
  isLayoutEditing?: boolean;
  isLoadingDashboards?: boolean;
  onDashboardAutoCreated: (dashboardId: string) => void;
  pendingStructuredInput?: ChartExploreContext | null;
  onClearPendingStructuredInput?: () => void;
}> = ({
  onClose,
  env,
  wsId,
  labelDictionaries,
  dashboardId,
  isLayoutEditing = false,
  isLoadingDashboards,
  onDashboardAutoCreated,
  pendingStructuredInput,
  onClearPendingStructuredInput,
}) => {
  const chat = useCaseAnalyticsChat({
    env,
    dashboardId,
  });
  const { data: dashboards } = useListDashboardsQuery(
    Realm.CASE_ANALYTICS,
    wsId,
  );
  const dashboardName = dashboards?.find((d) => d.id === dashboardId)?.name;
  const {
    dashboard,
    pinChart,
    unpinChart,
    isAtPinCap,
    isLoading,
    hasLoadFailed,
  } = useDashboard(Realm.CASE_ANALYTICS, wsId, dashboardId);

  const { mutateAsync: createDashboardAsync, isPending: isCreating } =
    useCreateDashboard(Realm.CASE_ANALYTICS, wsId);
  const { markPinned } = useJustPinned();

  const wrappedPinChart = async (
    definition: ChartDefinition,
    id?: string,
    title?: string,
  ) => {
    if (dashboardId) {
      pinChart(definition, id, title);
      return;
    }

    // No dashboard exists — auto-create one with this pin.
    const newDashboardId = v4();
    const pinId = id ?? v4();
    const pin: Pin = {
      id: pinId,
      title: title ?? null,
      layout: nextPinLayout([]),
      definition,
    };

    try {
      await createDashboardAsync({
        dashboardId: newDashboardId,
        members: [],
        name: "Untitled Dashboard",
        icon: "shield-halved",
        pins: { [pinId]: pin },
      });
      markPinned(pinId);
      onDashboardAutoCreated(newDashboardId);
    } catch (error) {
      logger.error(error);
      toastActions.failure({
        title: "Failed to create dashboard",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  };

  // The auto-create path doesn't touch the dashboard query, so a load error
  // only blocks pinning when a dashboard is selected.
  const loadFailed = hasLoadFailed && !!dashboardId;

  const pinDisabledReason = loadFailed
    ? "The dashboard failed to load, reload the page to pin charts"
    : isLayoutEditing
      ? "Pinning is disabled in edit mode, save your layout to add new charts"
      : isAtPinCap
        ? `Dashboards hold up to ${MAX_PINS} charts, remove one to pin a new chart`
        : undefined;

  const pinning: DashboardPinning = {
    dashboard,
    pinChart: wrappedPinChart,
    unpinChart,
    pinDisabled:
      isLoading ||
      loadFailed ||
      isAtPinCap ||
      isCreating ||
      !!isLoadingDashboards ||
      isLayoutEditing,
    pinDisabledReason,
  };

  return (
    <AnalyticsCopilotSidebar
      chat={chat}
      contextPill={
        dashboardName
          ? {
              icon: faChartMixed,
              label: dashboardName,
              key: "dashboard-context",
            }
          : undefined
      }
      dataLoc="case-analytics-copilot-sidebar"
      dataSource="case_analytics"
      emptyStateDataLoc="case-analytics-copilot-empty-state"
      labelDictionaries={labelDictionaries}
      onClearPendingStructuredInput={onClearPendingStructuredInput}
      onClose={onClose}
      pendingStructuredInput={pendingStructuredInput}
      pinning={pinning}
      storageKey="case-analytics-copilot-sidebar-width"
      suggestionPrompts={SUGGESTION_PROMPTS}
    />
  );
};
